<template>
  <div>
    <div class="space-y-4">
      <h2>{{ t('paymentTerm') }}</h2>
      <p>{{ t('paymentTermDescription') }}</p>
      <div class="grid grid-cols-3 gap-4">
        <InputField
          name="defaultPaymentTermDays"
          :label="t('defaultPaymentTermDays')"
          type="number"
          :min="0"
          :max="365"
          :max-fraction-digits="0"
        />
        <SelectField
          name="defaultPaymentTermDayType"
          :label="t('defaultPaymentTermDayType')"
          :options="paymentTermDayTypeOptions"
        />
      </div>
    </div>
    <Divider />
    <div class="flex justify-end">
      <CVButton variant="default" @click="handleSave">{{ t('save') }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  PaymentTermDayType,
  type UpdateYieldManagementSettingsDto,
} from '@carvia/ros-client-types';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import Divider from '@/components/Divider.vue';
import { useForm } from 'vee-validate';
import { cloneDeep } from 'lodash';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form.ts';
import { formHasChanged } from '@/hooks/use-form-has-changed.ts';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { yieldManagementDunningSchema } from '@/validation/yield-management-dunning.schema.ts';

const props = defineProps<{
  settingsDto: UpdateYieldManagementSettingsDto;
}>();

const emit = defineEmits<{
  (e: 'save', value: UpdateYieldManagementSettingsDto): void;
}>();

const { t } = useI18n();

const paymentTermDayTypeOptions = computed(() =>
  Object.values(PaymentTermDayType).map((value) => ({
    label: t(`paymentTermDayTypes.${value}`),
    value,
  })),
);

let initialValues = cloneDeep(props.settingsDto);

const { values: formValues, handleSubmit } = useForm({
  initialValues,
  validationSchema: yieldManagementDunningSchema,
});

const handleSave = handleSubmit((values) => {
  emit('save', values);
  initialValues = cloneDeep(formValues);
});

usePreventLeavingUnsavedForm(() => formHasChanged(initialValues, formValues));
</script>

<i18n lang="json">
{
  "en": {
    "paymentTerm": "Payment term",
    "paymentTermDescription": "Applies to every customer without their own payment term. It can be overridden per customer.",
    "defaultPaymentTermDays": "Default payment term",
    "defaultPaymentTermDayType": "Counted in",
    "paymentTermDayTypes": {
      "CALENDAR_DAYS": "Days",
      "BUSINESS_DAYS": "Business days"
    }
  },
  "de": {
    "paymentTerm": "Zahlungsziel",
    "paymentTermDescription": "Gilt für alle Kunden ohne eigenes Zahlungsziel. Es kann pro Kunde überschrieben werden.",
    "defaultPaymentTermDays": "Standard-Zahlungsziel",
    "defaultPaymentTermDayType": "Gezählt in",
    "paymentTermDayTypes": {
      "CALENDAR_DAYS": "Tage",
      "BUSINESS_DAYS": "Arbeitstage"
    }
  },
  "es": {
    "paymentTerm": "Plazo de pago",
    "paymentTermDescription": "Se aplica a todos los clientes sin plazo de pago propio. Se puede sobrescribir por cliente.",
    "defaultPaymentTermDays": "Plazo de pago predeterminado",
    "defaultPaymentTermDayType": "Contado en",
    "paymentTermDayTypes": {
      "CALENDAR_DAYS": "Días",
      "BUSINESS_DAYS": "Días hábiles"
    }
  }
}
</i18n>
